import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Zap, 
  Clock,
  Mail,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Power,
  PowerOff,
  Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const triggerIcons = {
  time_based: Clock,
  event: Zap
};

const AutomationsSection = ({ password }) => {
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [automationsRes, templatesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/automations?password=${password}`),
        axios.get(`${API_URL}/admin/templates?password=${password}`)
      ]);
      setAutomations(automationsRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kon data niet laden");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [password]);

  const handleToggleAutomation = async (automation) => {
    setTogglingId(automation.id);
    try {
      await axios.post(
        `${API_URL}/admin/automations/${automation.id}/toggle?password=${password}&active=${!automation.active}`
      );
      
      setAutomations(automations.map(a => 
        a.id === automation.id ? {...a, active: !a.active} : a
      ));
      
      toast.success(
        automation.active 
          ? `"${automation.name}" uitgeschakeld` 
          : `"${automation.name}" ingeschakeld`
      );
    } catch (error) {
      console.error("Error toggling automation:", error);
      toast.error("Kon automation niet wijzigen");
    } finally {
      setTogglingId(null);
    }
  };

  const getConditionText = (config) => {
    if (!config) return "";
    
    const conditions = {
      "report_not_purchased": "Rapport niet gekocht",
      "payment_success": "Na succesvolle betaling",
      "report_generated": "Na rapport generatie",
      "report_delivered": "Na rapport levering",
      "high_risk_not_purchased": "Hoog risico & niet gekocht"
    };
    
    if (config.delay_hours) {
      const hours = config.delay_hours;
      const days = hours >= 24 ? Math.floor(hours / 24) : 0;
      const timeText = days > 0 ? `${days} dag${days > 1 ? 'en' : ''}` : `${hours} uur`;
      return `${timeText} wachten, dan: ${conditions[config.condition] || config.condition}`;
    }
    
    return conditions[config.event] || config.event;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-blue-800">
            <strong>Automations</strong> versturen automatisch emails op basis van triggers. 
            Schakel ze in om je marketing te automatiseren.
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Tip: Begin met de transactionele emails (betaling & rapport) voordat je reminders activeert.
          </p>
        </div>
      </div>

      {/* Automations Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {automations.map((automation) => {
          const template = templates.find(t => t.id === automation.template_id);
          const TriggerIcon = triggerIcons[automation.trigger] || Zap;
          const isToggling = togglingId === automation.id;
          
          return (
            <motion.div
              key={automation.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white border rounded-xl p-5 transition-all ${
                automation.active 
                  ? "border-emerald-200 shadow-sm" 
                  : "border-zinc-200/60"
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    automation.active 
                      ? "bg-emerald-50 text-emerald-600" 
                      : "bg-zinc-100 text-zinc-400"
                  }`}>
                    <TriggerIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-900">{automation.name}</h3>
                    <p className="text-sm text-zinc-500">{automation.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {isToggling ? (
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  ) : (
                    <Switch
                      checked={automation.active}
                      onCheckedChange={() => handleToggleAutomation(automation)}
                    />
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {/* Trigger Info */}
                <div className="bg-zinc-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                    <Clock className="w-3 h-3" />
                    Trigger
                  </div>
                  <p className="text-sm text-zinc-700">
                    {getConditionText(automation.trigger_config)}
                  </p>
                </div>

                {/* Template Info */}
                <div className="bg-zinc-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mb-1">
                    <Mail className="w-3 h-3" />
                    Template
                  </div>
                  <p className="text-sm text-zinc-700">
                    {template?.name || automation.template_id}
                  </p>
                </div>

                {/* Status Badge */}
                <div className="flex items-center justify-between pt-2">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                    automation.active 
                      ? "bg-emerald-50 text-emerald-700" 
                      : "bg-zinc-100 text-zinc-500"
                  }`}>
                    {automation.active ? (
                      <>
                        <Power className="w-3 h-3" />
                        Actief
                      </>
                    ) : (
                      <>
                        <PowerOff className="w-3 h-3" />
                        Inactief
                      </>
                    )}
                  </div>
                  
                  {automation.emails_sent > 0 && (
                    <span className="text-xs text-zinc-500">
                      {automation.emails_sent} emails verzonden
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Note about automation execution */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-800">
            <strong>Let op:</strong> Time-based automations (herinneringen, follow-ups) vereisen een achtergrond job 
            die periodiek leads checkt. Momenteel worden alleen event-based automations (betaling, rapport) 
            direct getriggerd.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AutomationsSection;
